import { useIssueViewDialogState } from "./issue-view-dialog-state";

/**
 * The ONE storage key for the issue-view sidebar's dragged width. The drag is
 * authored app-side (`issue-view-dialog.tsx` mounts the `PanelResizeHandle`
 * particle), so the read and write helpers live here together and the key
 * cannot drift between them.
 */
const SIDEBAR_WIDTH_KEY = "__dialog-sidebar-width";

/** Cap on the default (never-dragged) sidebar width: 28rem. */
const SIDEBAR_DEFAULT_MAX = 448;
const SIDEBAR_MIN_PX = 320;
const SIDEBAR_MIN_RATIO = 0.25;
const SIDEBAR_MAX_RATIO = 0.66;

/** Drag bounds, resolved against the live viewport — the sheet is
 *  viewport-anchored, so the viewport width IS the container width. */
export function sidebarWidthBounds(containerWidth: number): {
  min: number;
  max: number;
} {
  return {
    min: Math.max(SIDEBAR_MIN_PX, containerWidth * SIDEBAR_MIN_RATIO),
    max: containerWidth * SIDEBAR_MAX_RATIO,
  };
}

/** The persisted width clamped to the current viewport, or the default. */
export function readSidebarWidth(containerWidth: number): number {
  const { min, max } = sidebarWidthBounds(containerWidth);
  let saved = 0;
  try {
    saved = Number(window.localStorage.getItem(SIDEBAR_WIDTH_KEY)) || 0;
  } catch {
    /* localStorage unavailable */
  }
  if (!Number.isFinite(saved) || saved <= 0) {
    return Math.min(Math.max(containerWidth * 0.85, min), SIDEBAR_DEFAULT_MAX);
  }
  return Math.min(Math.max(saved, min), max);
}

export function writeSidebarWidth(width: number): void {
  try {
    window.localStorage.setItem(SIDEBAR_WIDTH_KEY, String(width));
  } catch {
    /* localStorage unavailable */
  }
}

/**
 * Width in px of the open sidebar drawer that overlays the map. 0 if the
 * drawer is closed, in fullscreen, or has never been resized.
 */
export function useDrawerWidth(): number {
  const { state, desktopSize } = useIssueViewDialogState();
  if (!state || desktopSize !== "sidebar") return 0;
  if (typeof window === "undefined") return 0;
  try {
    const saved = window.localStorage.getItem(SIDEBAR_WIDTH_KEY);
    const n = saved ? Number(saved) : 0;
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}
